'use client';

import { useLenis } from 'lenis/react';
import { usePathname } from 'next/navigation';

/**
 * Custom hook to handle smooth anchor scrolling using Lenis.
 * Fixes issues where hash links don't trigger scrolling when already on the same page.
 */
export const useAnchorScroll = () => {
    const lenis = useLenis();
    const pathname = usePathname();

    const handleAnchorClick = (e, href) => {
        // Only handle links with a hash
        if (!href.includes('#')) return;

        const [path, hash] = href.split('#');
        const targetHash = `#${hash}`;

        // Normalize paths for comparison (remove trailing slashes, ensure starting slash)
        const normalize = (p) => {
            if (!p || p === '') return '/';
            return p.endsWith('/') ? p.slice(0, -1) : p;
        };

        const normalizedPath = normalize(path);
        const normalizedCurrentPath = normalize(pathname);

        // Check if we are already on the target page
        const isTargetPage = normalizedPath === normalizedCurrentPath;

        // Find the target element
        const targetElement = document.getElementById(hash);

        // ONLY intercept if we are on the target page AND the target element actually exists
        if (isTargetPage && targetElement && lenis) {
            e.preventDefault();
            
            // Allow small delay for mobile menu closing transitions
            setTimeout(() => {
                lenis.scrollTo(targetElement, {
                    offset: -100,
                    duration: 1.5,
                    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
                });
                
                // Update URL without jumping
                window.history.pushState(null, '', targetHash);
            }, 10);
        }
        // Otherwise, do nothing and let the standard <a> link handle navigation
    };

    return handleAnchorClick;
};
